import { useCallback, useEffect, useMemo, useState } from "react";

import { AUTH_CHANGE_EVENT, login } from "../components/utils/auth";
import AnalyticsPage from "./analytics/AnalyticsPage";
import AdminLayout from "./AdminLayout";
import CateringDetailsPage from "./catering/CateringDetailsPage";
import CateringPage from "./catering/CateringPage";
import CategoriesPage from "./categories/CategoriesPage";
import LoadingState from "./components/LoadingState";
import DashboardPage from "./dashboard/DashboardPage";
import useAdminNotifications from "./hooks/useAdminNotifications";
import LocationEditPage from "./locations/LocationEditPage";
import LocationsPage from "./locations/LocationsPage";
import MenuItemsPage from "./menu/MenuItemsPage";
import RegularMenuPage from "./menu/RegularMenuPage";
import WeeklyMenuPage from "./menu/WeeklyMenuPage";
import OrderDetailsPage from "./orders/OrderDetailsPage";
import SettingsPage from "./settings/SettingsPage";
import OrdersPage from "./orders/OrdersPage";
import {
  API_ROOT,
  adminApi,
  clearAdminToken,
  getAdminToken,
} from "./services/adminApi";
import "./AdminPanel.css";

function usePathNavigation() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((path) => {
    window.history.pushState({}, "", path);
    setCurrentPath(path);
  }, []);

  return [currentPath, navigate];
}

function LoginPage({ initialError = "", onLogin }) {
  const [error, setError] = useState(initialError);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");

  useEffect(() => {
    setError(initialError);
  }, [initialError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await login({ username, password });
      onLogin();
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="admin-lock">
      <section className="card admin-lock-card">
        <form className="card-body flex flex-column gap-4" onSubmit={handleSubmit}>
          <p className="eyebrow">Zayas Kitchen Admin</p>
          <h1>Staff sign in</h1>

          {error ? <div className="alert alert-error">{error}</div> : null}

          <div className="form-group">
            <label className="form-label" htmlFor="admin-username">
              Username
            </label>
            <input
              id="admin-username"
              autoComplete="username"
              required
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              autoComplete="current-password"
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          <button
            className="btn btn-primary"
            aria-busy={isSubmitting ? "true" : "false"}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Signing in" : "Sign In"}
          </button>

          <p className="small-text muted-text">API root: {API_ROOT}</p>
        </form>
      </section>
    </main>
  );
}

function getOrderIdFromPath(path) {
  const match = path.match(/^\/admin\/orders\/(\d+)/);
  return match ? match[1] : null;
}

function getLocationIdFromPath(path) {
  const match = path.match(/^\/admin\/locations\/(new|\d+)\/edit|^\/admin\/locations\/new/);
  if (!match) {
    return null;
  }
  return match[1] || "new";
}

function getCateringRequestIdFromPath(path) {
  const match = path.match(/^\/admin\/catering\/(\d+)/);
  return match ? match[1] : null;
}

export default function AdminRoutes() {
  const [authVersion, setAuthVersion] = useState(0);
  const [currentPath, navigate] = usePathNavigation();
  const [authNotice, setAuthNotice] = useState("");
  const [filters, setFilters] = useState({
    location: "",
    range: "today",
    search: "",
  });
  const [locations, setLocations] = useState([]);

  const isAuthenticated = Boolean(getAdminToken());
  const { toasts, unreadCount } = useAdminNotifications(isAuthenticated);

  useEffect(() => {
    function handleAuthChange(event) {
      const reason = event.detail?.reason || "";
      const hasToken = Boolean(getAdminToken());

      if (!hasToken) {
        setLocations([]);

        if (reason === "expired") {
          setAuthNotice("Your session expired. Please sign in again.");
        }
      }

      setAuthVersion((version) => version + 1);
    }

    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLocations([]);
      return;
    }

    adminApi("/locations/")
      .then((payload) => setLocations(payload.results || payload || []))
      .catch((requestError) => {
        if (requestError.status === 401) {
          setAuthNotice("Your session expired. Please sign in again.");
          clearAdminToken();
          setAuthVersion((version) => version + 1);
          return;
        }

        console.error(requestError);
      });
  }, [isAuthenticated, authVersion]);

  const updateFilters = (changes) => {
    setFilters((currentFilters) => ({ ...currentFilters, ...changes }));
  };

  const content = useMemo(() => {
    const orderId = getOrderIdFromPath(currentPath);
    const cateringRequestId = getCateringRequestIdFromPath(currentPath);

    if (orderId) {
      return <OrderDetailsPage orderId={orderId} />;
    }

    if (cateringRequestId) {
      return <CateringDetailsPage requestId={cateringRequestId} />;
    }

    if (currentPath.startsWith("/admin/orders")) {
      return (
        <OrdersPage
          filters={filters}
          onFilterChange={updateFilters}
          onNavigate={navigate}
        />
      );
    }

    const locationId = getLocationIdFromPath(currentPath);

    if (locationId) {
      return <LocationEditPage locationId={locationId} onNavigate={navigate} />;
    }

    if (currentPath.startsWith("/admin/categories")) {
      return <CategoriesPage />;
    }

    if (currentPath.startsWith("/admin/menu-items")) {
      return <MenuItemsPage filters={filters} />;
    }

    if (currentPath.startsWith("/admin/weekly-menu")) {
      return <WeeklyMenuPage filters={filters} />;
    }

    if (currentPath.startsWith("/admin/regular-menu")) {
      return <RegularMenuPage />;
    }

    if (currentPath.startsWith("/admin/locations")) {
      return <LocationsPage onNavigate={navigate} />;
    }

    if (currentPath.startsWith("/admin/catering")) {
      return <CateringPage filters={filters} />;
    }

    if (currentPath.startsWith("/admin/analytics")) {
      return <AnalyticsPage filters={filters} />;
    }

    if (currentPath.startsWith("/admin/settings")) {
      return <SettingsPage />;
    }

    return <DashboardPage filters={filters} onNavigate={navigate} />;
  }, [currentPath, filters, navigate]);

  if (!isAuthenticated) {
    return (
      <LoginPage
        initialError={authNotice}
        onLogin={() => {
          setAuthNotice("");
          setAuthVersion((version) => version + 1);
        }}
      />
    );
  }

  if (!content) {
    return <LoadingState />;
  }

  return (
    <AdminLayout
      currentPath={currentPath}
      filters={filters}
      locations={locations}
      toasts={toasts}
      unreadCount={unreadCount}
      onFilterChange={updateFilters}
      onLogout={() => {
        clearAdminToken();
        setAuthVersion((version) => version + 1);
      }}
      onNavigate={navigate}
    >
      {content}
    </AdminLayout>
  );
}